import React, { useMemo, useCallback, useContext } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  Text,
  Button,
  Card,
  CardPreview,
  CardHeader,
  CardFooter,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
} from '@fluentui/react-components';
import {
  EditRegular, DeleteRegular,
} from '@fluentui/react-icons';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { STEP_PATHS } from '../../../../step-constants';

const getStyles = () => ({
  adThumbailRoot: {
    height: '236px',
    width: '212px',
    borderRadius: '6px',
    boxShadow: '0px 2px 4px 0px #00000024',
    '&:hover': {
      '& .hover-buttons': {
        display: 'flex',
        background: 'linear-gradient(180deg, rgba(49, 34, 34, 0.6) 0%, rgba(49, 34, 34, 0.9) 52%, #312222 100%)',
      },
    },
    '&:focus': {
      '& .hover-buttons': {
        display: 'flex',
        background: 'linear-gradient(180deg, rgba(49, 34, 34, 0.6) 0%, rgba(49, 34, 34, 0.9) 52%, #312222 100%)',
      },
    },
  },
  cardPreview: {
    height: '118px',
    width: '212px',
  },
  adLabel: {
    borderRadius: '4px',
    border: '0.5px solid black',
    textAlign: 'center',
    fontSize: '10px',
    height: 16,
    width: 21,

  },
  description: {
    overflow: 'hidden !important',
    textOverflow: 'ellipsis !important',
    height: '40px',
    width: '180px',
    display: '-webkit-box !important',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  header: {
    width: '180px',
    overflow: 'hidden !important',
    textOverflow: 'ellipsis !important',
    textWrap: 'nowrap !important',
  },
  hoverButtons: {
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    rowGap: '5px',
    flexDirection: 'column',
    zIndex: 10,
  },
  hoverButton: {
    border: 'none !important',
    backgroundColor: 'rgba(255, 255, 255, 0.09) !important',
    color: 'white !important',
    '&:hover': {
      backgroundColor: 'rgba(0, 120, 212, 1) !important',
    },
    '&:focus': {
      backgroundColor: 'rgba(0, 120, 212, 1) !important',
    },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: '5px',
  },
});

export const AdThumbnail = ({
  item,
  onDeleteAdClicked,
  idx,
}) => {
  const classes = useDefaultStyles(getStyles);

  const { send } = useWizardService();

  const i18n = useContext(I18nContext);

  const adTitle = useMemo(() => item && item.Headlines && item.Headlines.Headline0, [item]);

  const adId = useMemo(() => item && item.Id, [item]);

  const adDescription = useMemo(() => item && item.Descriptions && item.Descriptions.Description0, [item]);

  const businessName = useMemo(() => item && item.BusinessName, [item]);

  const adImageUrl = useMemo(() => item && item.Images && item.Images.ImageList && item.Images.ImageList[0] && item.Images.ImageList[0][0].src, [item]);

  const onEditAdClick = useCallback((editingAdId) => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
      editingAdId,
    });

    send(WIZARD_EVENTS.NAVIGATE_TO, {
      stepId: STEP_PATHS.MULTI_NATIVE_AD,
    });
  }, [send]);

  const onDeleteAd = useCallback((deletingAdId) => {
    onDeleteAdClicked(deletingAdId);
  }, [onDeleteAdClicked]);

  return (
    <Dialog>
      <Card tabIndex="0" className={classNames([classes.adThumbailRoot, 'ad-thumbnail', `ad-thumbnail-${idx}`])}>
        <CardPreview className={classes.cardPreview}>
          <img src={adImageUrl} alt="" />
        </CardPreview>
        <CardHeader
          header={<Text className={classes.header} weight="semibold">{adTitle}</Text>}
          description={<Text className={classes.description}>{adDescription}</Text>}
        />
        <CardFooter>
          <div className={classes.footer}> <span className={classes.adLabel}> Ad </span>{ businessName } </div>
        </CardFooter>
        <div className={classNames([classes.hoverButtons, 'hover-buttons'])}>
          <Button tabIndex="0" shape="circular" onClick={() => onEditAdClick(adId)} className={classNames([classes.hoverButton, 'edit-button'])} icon={<EditRegular />}> {i18n.getString(_TL_('Edit'))} </Button>
          <DialogTrigger disableButtonEnhancement>
            <Button tabIndex="0" shape="circular" className={classNames([classes.hoverButton, 'delete-button'])} icon={<DeleteRegular />}> {i18n.getString(_TL_('Delete'))} </Button>
          </DialogTrigger>
        </div>
      </Card>
      <DialogSurface>
        <DialogBody className="delete-ad-dialog">
          <DialogTitle> {i18n.getString(_TL_('Delete this Ad?'))} </DialogTitle>
          <DialogContent>
            {i18n.getString(_TL_('This ad will be removed from this campaign. There is no way to undo this action.'))}
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" className="delete-ad-button" onClick={() => onDeleteAd(adId)}>{i18n.getString(_TL_('Delete Ad'))}</Button>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" className="cancel-delete-ad-button">{i18n.getString(_TL_('Cancel'))}</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

AdThumbnail.propTypes = {
  item: PropTypes.shape({
    Id: PropTypes.string,
    Headlines: PropTypes.shape({
      Headline0: PropTypes.string,
    }),
    Descriptions: PropTypes.shape({
      Description0: PropTypes.string,
    }),
    BusinessName: PropTypes.string,
    Images: PropTypes.shape({
      ImageList: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({
        src: PropTypes.string,
      }))),
    }),
  }).isRequired,
  onDeleteAdClicked: PropTypes.func.isRequired,
  idx: PropTypes.number.isRequired,
};
